// ============================================================================
// Shared Component: JobFormFields
// ============================================================================
// Renders all job form fields in a consistent layout.
// Used by both JobDetailModal and MissingInvalidData/JobEditForm.
//
// IMPORT_FIELDS: If you are adding a new column, add it here.
// This is the single source of truth for form field layout.

import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import type { Branch, Job } from "../../../api/types";
import type { ImportTableColumn } from "../../../utilities/types";
import { getJobPriorities, JobsPriority } from "../../../utilities/types";
import type { ColumnContext } from "../ImportTable/columns/standard-column-types/ColumnContext";
import type { DropdownOptions } from "./useDropdownOptions";

// Mobile column components
import { TextColumnMobile } from "../ImportTable/columns/standard-column-types/textColumn/textColumn.mobile";
import { SelectColumnMobile } from "../ImportTable/columns/standard-column-types/selectColumn/selectColumn.mobile";
import { CheckboxColumnMobile } from "../ImportTable/columns/standard-column-types/checkboxColumn/checkboxColumn.mobile";
import { DateColumnMobile } from "../ImportTable/columns/standard-column-types/dateColumn/dateColumn.mobile";
import { GeoJSONFileColumnMobile } from "../ImportTable/columns/standard-column-types/geoJSONFileColumn/geoJSONFileColumn.mobile";
import { DealColumnMobile } from "../ImportTable/columns/Deal.column/Deal.column.mobile";
import { SalespersonColumnMobile } from "../ImportTable/columns/Salesperson.column/Salesperson.column.mobile";
import { SalespersonPhoneColumnMobile } from "../ImportTable/columns/SalespersonPhone.column/SalespersonPhone.column.mobile";
import { LastCropColumnMobile } from "../ImportTable/columns/LastCrop.column/LastCrop.column.mobile";
import { NextCropColumnMobile } from "../ImportTable/columns/NextCrop.column/NextCrop.column.mobile";
import { FieldNotesColumnMobile } from "../ImportTable/columns/FieldNotes.column/FieldNotes.column.mobile";

// ============================================================================
// Types
// ============================================================================

export interface JobFormFieldsProps {
	/** Current job being edited */
	job: Job;
	/** Column context for mobile columns */
	columnContext: ColumnContext;
	/** Dropdown options for select fields */
	dropdownOptions: DropdownOptions;
	/** Available branches for the branch dropdown */
	branches?: Branch[];
	/** Function to check if a field is visible */
	isVisible: (columnId: ImportTableColumn) => boolean;
	/** Whether this is bulk edit mode (disables required indicators, shows mixed value placeholders) */
	isBulkEdit?: boolean;
	/** Function to check if a field has mixed values across selected jobs (only used in bulk edit) */
	hasMixedValues?: (field: keyof Job) => boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Renders all job form fields in a consistent layout.
 *
 * This component is the single source of truth for form field ordering and layout.
 * Both JobDetailModal and MissingInvalidData/JobEditForm use this component.
 *
 * In bulk edit mode (isBulkEdit=true):
 * - Required indicators are hidden
 * - Fields with mixed values show placeholder text
 */
export function JobFormFields({
	job,
	columnContext,
	dropdownOptions,
	branches = [],
	isVisible,
	isBulkEdit = false,
	hasMixedValues,
}: JobFormFieldsProps) {
	const { gridSizes, depths, labPackages, micros } = dropdownOptions;

	// Helper to get placeholder for mixed values
	const getMixedPlaceholder = (field: keyof Job): string | undefined => {
		if (isBulkEdit && hasMixedValues?.(field)) {
			return "Multiple values";
		}
		return undefined;
	};

	// In bulk edit mode, don't mark fields as required
	const isRequired = (defaultRequired: boolean): boolean => {
		return isBulkEdit ? false : defaultRequired;
	};

	return (
		<Stack spacing={2}>
			{/* Basic Information - Grower, Farm, Field side-by-side */}
			<Grid container spacing={2}>
				{isVisible("grower") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importGrowerName"
							columnId="grower"
							required={isRequired(true)}
							placeholder={getMixedPlaceholder("importGrowerName")}
						/>
					</Grid>
				)}
				{isVisible("farm") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importFarmName"
							columnId="farm"
							placeholder={getMixedPlaceholder("importFarmName")}
						/>
					</Grid>
				)}
				{isVisible("field") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importFieldName"
							columnId="field"
							required={isRequired(true)}
							placeholder={getMixedPlaceholder("importFieldName")}
						/>
					</Grid>
				)}
			</Grid>

			{/* Attachments - Boundary, Points, Zones side-by-side */}
			<Grid container spacing={2}>
				{isVisible("boundaryGeoJSON") && (
					<Grid size="grow">
						<GeoJSONFileColumnMobile
							job={job}
							columnContext={columnContext}
							optionsKey="boundaryOptions"
							geoJSONKey="boundaryGeoJSON"
							columnId="boundaryGeoJSON"
						/>
					</Grid>
				)}
				{isVisible("pointsGeoJSON") && (
					<Grid size="grow">
						<GeoJSONFileColumnMobile
							job={job}
							columnContext={columnContext}
							optionsKey="pointsOptions"
							geoJSONKey="pointsGeoJSON"
							columnId="pointsGeoJSON"
						/>
					</Grid>
				)}
				{isVisible("zonesGeoJSON") && (
					<Grid size="grow">
						<GeoJSONFileColumnMobile
							job={job}
							columnContext={columnContext}
							optionsKey="zonesOptions"
							geoJSONKey="zonesGeoJSON"
							columnId="zonesGeoJSON"
						/>
					</Grid>
				)}
			</Grid>

			{/* Field Notes - full width */}
			{isVisible("fieldNotes") && <FieldNotesColumnMobile job={job} columnContext={columnContext} />}

			{/* Order - Ready to Sample and Est Field Ready Date side-by-side */}
			<Grid container spacing={2}>
				{isVisible("isReadyToSample") && (
					<Grid size="grow">
						<CheckboxColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="isReadyToSample"
							columnId="isReadyToSample"
						/>
					</Grid>
				)}
				{isVisible("dateFieldReadyEstimated") && (
					<Grid size="grow">
						<DateColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="dateFieldReadyEstimated"
							columnId="dateFieldReadyEstimated"
						/>
					</Grid>
				)}
			</Grid>

			{/* Field Priority and Collect Boundary */}
			<Grid container spacing={2}>
				{isVisible("collectBoundary") && (
					<Grid size="grow">
						<CheckboxColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="collectBoundary"
							columnId="collectBoundary"
						/>
					</Grid>
				)}
				{isVisible("fieldPriority") && (
					<Grid size="grow">
						<SelectColumnMobile<string>
							job={job}
							columnContext={columnContext}
							fieldName="fieldPriority"
							columnId="fieldPriority"
							options={[...getJobPriorities()]}
							freeSolo={false}
							getValue={(j) => j.fieldPriority || "Regular Turn (default)"}
							setValue={(j, value) => {
								j.fieldPriority = (value || "Regular Turn (default)") as JobsPriority;
							}}
						/>
					</Grid>
				)}
			</Grid>

			{/* Submission Notes - full width */}
			{isVisible("submissionNotes") && (
				<TextColumnMobile
					job={job}
					columnContext={columnContext}
					fieldName="submissionNotes"
					columnId="submissionNotes"
					multiline
					rows={3}
				/>
			)}

			{/* Deal - Density, Depth, Freq of Micros, Lab Name, Test Package side-by-side */}
			<Grid container spacing={2}>
				{isVisible("density") && (
					<Grid size="grow">
						<SelectColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importDensity"
							columnId="density"
							options={gridSizes}
							isNumber
							required={isRequired(true)}
						/>
					</Grid>
				)}
				{isVisible("depth") && (
					<Grid size="grow">
						<SelectColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importDepth"
							columnId="depth"
							options={depths}
							isNumber
							required={isRequired(true)}
						/>
					</Grid>
				)}
				{isVisible("frequencyOfMicros") && (
					<Grid size="grow">
						<SelectColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="externalFrequencyOfMicros"
							columnId="frequencyOfMicros"
							options={micros}
							isNumber
						/>
					</Grid>
				)}
				{isVisible("labName") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importLabName"
							columnId="labName"
							placeholder={getMixedPlaceholder("importLabName")}
						/>
					</Grid>
				)}
				{isVisible("testPackage") && (
					<Grid size="grow">
						<SelectColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="externalTestPackageId"
							columnId="testPackage"
							options={labPackages}
						/>
					</Grid>
				)}
			</Grid>

			{/* Deal Configuration - full width */}
			{isVisible("deal") && <DealColumnMobile job={job} columnContext={columnContext} required={isRequired(true)} />}

			{/* Lab Info */}
			{isVisible("labInstructions") && (
				<TextColumnMobile
					job={job}
					columnContext={columnContext}
					fieldName="labInstructions"
					columnId="labInstructions"
					multiline
					rows={2}
				/>
			)}
			{isVisible("microsList") && (
				<TextColumnMobile
					job={job}
					columnContext={columnContext}
					fieldName="microsList"
					columnId="microsList"
					disabled={!job?.files?.pointsGeoJSON?.features?.length}
				/>
			)}

			{/* Last Crop, Next Crop, and Yield Goal side-by-side */}
			<Grid container spacing={2}>
				{isVisible("lastCrop") && (
					<Grid size="grow">
						<LastCropColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
				{isVisible("nextCrop") && (
					<Grid size="grow">
						<NextCropColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
				{isVisible("yieldGoal") && (
					<Grid size="grow">
						<TextColumnMobile job={job} columnContext={columnContext} fieldName="yieldGoal" columnId="yieldGoal" />
					</Grid>
				)}
			</Grid>

			{/* Contacts - Branch full width */}
			{isVisible("branch") && (
				<SelectColumnMobile<Branch>
					job={job}
					columnContext={columnContext}
					fieldName="branchIds"
					columnId="branch"
					options={branches}
					freeSolo={false}
					getOptionLabel={(branch) => branch.name}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					getValue={(j) => branches.find((branch) => j.branchIds?.includes(branch.id)) || null}
					setValue={(j, branch) => {
						j.branchIds = branch ? [branch.id] : [];
					}}
				/>
			)}

			{/* Salesperson and Salesperson Phone side-by-side */}
			<Grid container spacing={2}>
				{isVisible("salesperson") && (
					<Grid size="grow">
						<SalespersonColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
				{isVisible("salespersonMobilePhone") && (
					<Grid size="grow">
						<SalespersonPhoneColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
			</Grid>

			{/* Grower Phone - full width */}
			{isVisible("growerPhoneNumber") && (
				<TextColumnMobile
					job={job}
					columnContext={columnContext}
					fieldName="growerPhoneNumber"
					columnId="growerPhoneNumber"
					inputType="tel"
				/>
			)}

			{/* Submitter Name and Email side-by-side */}
			<Grid container spacing={2}>
				{isVisible("submitterName") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="submitterName"
							columnId="submitterName"
						/>
					</Grid>
				)}
				{isVisible("submitterEmail") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="submitterEmail"
							columnId="submitterEmail"
							inputType="email"
						/>
					</Grid>
				)}
			</Grid>

			{/* Sync Codes - Event ID and Lab Submission Code side-by-side */}
			<Grid container spacing={2}>
				{isVisible("eventId") && (
					<Grid size="grow">
						<TextColumnMobile job={job} columnContext={columnContext} fieldName="eventId" columnId="eventId" />
					</Grid>
				)}
				{isVisible("labSubmissionCode") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="labSubmissionCode"
							columnId="labSubmissionCode"
						/>
					</Grid>
				)}
			</Grid>

			{/* Field ID - full width */}
			{isVisible("fieldId") && (
				<TextColumnMobile job={job} columnContext={columnContext} fieldName="fieldId" columnId="fieldId" />
			)}

			{/* External ID, External Farm ID, External Lab ID side-by-side */}
			<Grid container spacing={2}>
				{isVisible("externalId") && (
					<Grid size="grow">
						<TextColumnMobile job={job} columnContext={columnContext} fieldName="externalId" columnId="externalId" />
					</Grid>
				)}
				{isVisible("externalFarmId") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="externalFarmId"
							columnId="externalFarmId"
						/>
					</Grid>
				)}
				{isVisible("externalLabId") && (
					<Grid size="grow">
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="externalLabId"
							columnId="externalLabId"
						/>
					</Grid>
				)}
			</Grid>

			{/* Misc */}
			{isVisible("isTestJob") && (
				<CheckboxColumnMobile job={job} columnContext={columnContext} fieldName="isTestJob" columnId="isTestJob" />
			)}
		</Stack>
	);
}
